/**
 * RunRdpAccessPanel — NC-1579.
 *
 * Shows and edits the public-RDP whitelist of a RUNNING training run: a tutor
 * rarely knows the participants' source IPs when creating the run, so the list
 * must be editable afterwards. "Meine aktuelle IP übernehmen" asks the API for
 * the address the browser reaches the portal from (GET /api/auth/client-ip) and
 * adds it as /32 — the one address a tutor never knows by heart.
 *
 * Saving calls POST /api/runs/{runId}/rdp (operator+). The API dispatches
 * run-rdp-toggle.yml; 152 updates the NSG rule and writes `access` back, so the
 * panel reflects the CONFIRMED state from the run doc and shows the transitional
 * `rdpToggleState` / `rdpToggleError` instead of an optimistic guess (NC-1212).
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MyLocation as MyLocationIcon } from '@mui/icons-material';
import { networkApi, runsApi, extractErrorMessage } from '../services/api';
import { Run } from '../services/types';
import {
  DEFAULT_RDP_SOURCE_CIDR,
  MAX_RDP_SOURCE_CIDRS,
  normalizeRdpSourceCidr,
} from '../utils/rdpSourceCidr';

export const MY_IP_LABEL = 'Meine aktuelle IP übernehmen';

type RdpRun = Pick<Run, 'runId' | 'status' | 'access' | 'rdpToggleState' | 'rdpToggleError' | 'rdpToggleJobUrl'>;

interface RunRdpAccessPanelProps {
  run: RdpRun;
  /** operator+ may edit; viewers see the state read-only. */
  canEdit: boolean;
  /** Re-fetch the run so the panel shows what 152 confirmed. */
  onChanged?: () => void;
}

function sameList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((c) => b.includes(c));
}

export function RunRdpAccessPanel({ run, canEdit, onChanged }: RunRdpAccessPanelProps) {
  const enabled = run.access?.publicRdp === true;
  const confirmed = run.access?.allowedSourceCidrs ?? [];
  const confirmedKey = confirmed.join(',');

  const [draft, setDraft] = useState<string[]>(confirmed);
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'save' | 'disable' | 'myip' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // A re-fetched run (e.g. after 152 finished) replaces the draft.
  useEffect(() => {
    setDraft(confirmedKey ? confirmedKey.split(',') : []);
  }, [confirmedKey]);

  const isActive = run.status === 'active';
  const pending = !!run.rdpToggleState;
  const editable = canEdit && isActive && !pending && busy === null;
  const dirty = !enabled || !sameList(draft, confirmed);

  const addCidr = (raw: string): boolean => {
    const check = normalizeRdpSourceCidr(raw);
    if (!check.ok) {
      setInputError(check.error);
      return false;
    }
    if (!draft.includes(check.cidr)) {
      if (draft.length >= MAX_RDP_SOURCE_CIDRS) {
        setInputError(`Höchstens ${MAX_RDP_SOURCE_CIDRS} IP-Adressen pro Schulung.`);
        return false;
      }
      setDraft([...draft, check.cidr]);
    }
    setInputError(null);
    return true;
  };

  const handleAdd = () => {
    if (addCidr(input)) setInput('');
  };

  const handleMyIp = async () => {
    setBusy('myip');
    setError(null);
    try {
      const { ip, cidr } = await networkApi.getClientIp();
      if (addCidr(cidr)) setInfo(`Deine aktuelle IP ${ip} wurde übernommen — zum Anwenden „Freigabe speichern“.`);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const apply = async (nextEnabled: boolean) => {
    setBusy(nextEnabled ? 'save' : 'disable');
    setError(null);
    setInfo(null);
    try {
      const result = await runsApi.setRdpAccess(run.runId, nextEnabled, nextEnabled ? draft : []);
      setInfo(
        result.noop
          ? 'Keine Änderung — die Freigabe ist bereits so eingestellt.'
          : 'Wird angewendet — das dauert etwa 1–2 Minuten. Die Anzeige aktualisiert sich danach.'
      );
      onChanged?.();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Öffentlicher RDP-Zugang
        </Typography>

        <Typography variant="body2" sx={{ mb: 1 }}>
          Status: <strong>{enabled ? 'aktiv' : 'aus'}</strong>
          {pending && ` — ${run.rdpToggleState === 'disabling' ? 'wird abgeschaltet' : 'wird angewendet'} …`}
        </Typography>

        {run.rdpToggleError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Die letzte Änderung ist fehlgeschlagen: {run.rdpToggleError}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary">
          Verbindungen per RDP sind nur von diesen IP-Adressen erlaubt:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }} data-testid="rdp-cidr-list">
          {draft.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {enabled ? '—' : `Keine — beim Speichern gilt der Firmen-Standard (${DEFAULT_RDP_SOURCE_CIDR}).`}
            </Typography>
          ) : (
            draft.map((cidr) => (
              <Chip
                key={cidr}
                aria-label={`Freigegebene IP ${cidr}`}
                label={cidr}
                size="small"
                onDelete={editable ? () => setDraft(draft.filter((c) => c !== cidr)) : undefined}
              />
            ))
          )}
        </Box>

        {canEdit && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}>
              <TextField
                size="small"
                label="IP-Adresse hinzufügen"
                placeholder="z. B. 203.0.113.42"
                value={input}
                disabled={!editable}
                error={!!inputError}
                helperText={inputError ?? undefined}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
              />
              <Button variant="outlined" onClick={handleAdd} disabled={!editable || !input.trim()}>
                Hinzufügen
              </Button>
              <Tooltip title={MY_IP_LABEL}>
                <span>
                  <IconButton aria-label={MY_IP_LABEL} onClick={handleMyIp} disabled={!editable}>
                    {busy === 'myip' ? <CircularProgress size={18} /> : <MyLocationIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button variant="contained" onClick={() => apply(true)} disabled={!editable || !dirty}>
                {busy === 'save' ? 'Wird gespeichert …' : enabled ? 'Freigabe speichern' : 'RDP-Zugang aktivieren'}
              </Button>
              {enabled && (
                <Button color="warning" onClick={() => apply(false)} disabled={!editable}>
                  {busy === 'disable' ? 'Wird abgeschaltet …' : 'Öffentlichen Zugang abschalten'}
                </Button>
              )}
            </Box>

            {!isActive && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Nur bei einer laufenden Schulung änderbar.
              </Typography>
            )}
          </>
        )}

        {info && (
          <Alert severity="info" sx={{ mt: 2 }} onClose={() => setInfo(null)}>
            {info}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default RunRdpAccessPanel;
